import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../../hooks";
import { useEffect, useState } from "react";
import { UploadMonitoringRecordsResponse } from "../../../types/interfaces/file/imports/monitoringRecords";
import { confirmMonitoringRecordsFile, uploadMonitoringRecordsFile } from "../../../api/imports";
import { Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { getErrorMessage } from "../../../utils";
import { UploadMonitoringRecordsPreviewTable } from "./UploadMonitoringRecordsPreviewTable";

interface MonitoringConfirmImportDialogProps {
  captureId: number;
  open: boolean;
  file: File;
  onClose: () => void;
  onSuccess: () => void;
}

export const MonitoringConfirmImportDialog = ({
  captureId,
  open,
  file,
  onClose,
  onSuccess,
}: MonitoringConfirmImportDialogProps) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [result, setResult] = useState<UploadMonitoringRecordsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!open) return;
    
    const upload = async () => {
      setLoading(true);
      setHasError(false);
      try {
        const res = await uploadMonitoringRecordsFile(captureId, { file });
        setResult(res);
      } catch (error) {
        handleError(error);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    upload();
  }, [open, file]);

  const validProcessedMonitoringRecords =
    result?.lines
      .filter((l) => l.status === "ok" && l.preview)
      .map((l) => l.preview!) ?? [];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth={false}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: "55%",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>{t("imports.confirm-dialog.title")}</DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && hasError && (
          <Alert severity="error">
            <AlertTitle>{t("imports.confirm-dialog.error-title")}</AlertTitle>
            {t("generic.errors.something-went-wrong")}
          </Alert>
        )}

        {!loading && !hasError && result && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography>
              {t("imports.confirm-dialog.description")} <b>{file?.name}</b>
            </Typography>

            <Alert severity={result.invalid > 0 ? "warning" : "success"}>
              <AlertTitle>
                {t("imports.confirm-dialog.summary-title")}
              </AlertTitle>
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Chip
                  label={`${t("imports.confirm-dialog.total")}: ${result.total}`}
                />
                {result.valid > 0 && (
                  <Chip
                    color="success"
                    label={`${t("imports.confirm-dialog.valid")}: ${result.valid}`}
                  />
                )}
                {result.invalid > 0 && (
                  <Chip
                    color="error"
                    label={`${t("imports.confirm-dialog.invalid")}: ${result.invalid}`}
                  />
                )}
              </Box>
            </Alert>

            {/* Errors */}
            {result.invalid > 0 && (
              <Alert severity="error">
                <AlertTitle>
                  {t("imports.confirm-dialog.errors-title")}
                </AlertTitle>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  {result.lines
                    .filter((l) => l.status === "error")
                    .map((line) => (
                      <Box key={line.line}>
                        <Typography variant="body2" fontWeight={500}>
                          {t("imports.confirm-dialog.error-line", {
                            line: line.line,
                          })}
                        </Typography>

                        {line.errors?.map((err) => (
                          <Typography
                            key={`${line.line}-${err.code}-${err.field ?? ""}`}
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1.5 }}
                          >
                            • {getErrorMessage(err.code, t, err.field)}
                          </Typography>
                        ))}
                      </Box>
                    ))}
                </Box>
              </Alert>
            )}

            {/* Preview */}
            <UploadMonitoringRecordsPreviewTable lines={result.lines} />

            <Typography variant="caption" color="text.secondary">
              {t("imports.confirm-dialog.warning")}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {hasError ? t("generic.close") : t("generic.back")}
        </Button>

        {!loading && !hasError && (
          <Button
            variant="contained"
            disabled={validProcessedMonitoringRecords.length === 0}
            onClick={async () => {
              await confirmMonitoringRecordsFile(
                captureId,
                {
                  processedMonitorings: validProcessedMonitoringRecords,
                }
              );
              onSuccess();
            }}
          >
            {t("generic.confirm")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}